<template>
  <div :id="documentPageElementId" class="document-content-page-container">
    <div class="document-content-page" data-external="signing-page-document-preview">
      <div v-if="imageError" class="document-content-error-page">
        <FriedH2 class="error-message">{{ t('could-not-load-page') }}</FriedH2>
      </div>
      <div v-else>
        <img
          v-if="page"
          class="document-content-page-loader"
          :src="thumbUrl"
          :alt="imageDescription(page.pageNum)"
          @load="handleLoaded"
          @error="handleImgError"
        />
      </div>
      <template v-if="showFields">
        <Field
          v-for="(field, index) in fields"
          :id="field.id"
          :key="field.id"
          :data-external="`document-content-page-field-container-type:${field.type}-index:${index}`"
          :field="field"
          :recipient="recipient"
          :all-recipients="allRecipients"
          :sender="sender"
          :focused-required-id="focusedRequiredId"
          :is-disabled="disableFields"
          :required-steps-left="requiredStepsLeft"
          :is-signed="isSigned"
          :initials="initials"
          :verified-initial-field-ids="verifiedInitialFieldIds"
          @update="handleUpdateField"
          @click-initials-field="handleClickInitialsField"
          @click-link-field="handleClickLinkField"
          @click-sign-field="handleClickSignField"
        />
      </template>
    </div>
  </div>
</template>
<script lang="ts">
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { SigningPageUser } from '@getaccept/lib-shared/src/types/signing-page-user';
import type { PropType } from 'vue';
import { defineComponent, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ManuallySignedPage } from '@getaccept/lib-shared/src/types/manually-signed-page';
import Field from '../fields/components/Field.vue';

export default defineComponent({
  components: {
    Field,
  },
  props: {
    page: {
      type: Object as PropType<DocumentPage | ManuallySignedPage>,
      default: null,
    },
    pageWidth: {
      type: Number,
      default: undefined,
    },
    recipient: {
      type: Object as PropType<Recipient>,
      default: undefined,
    },
    allRecipients: {
      type: Array as PropType<Recipient[]>,
      default: () => [],
    },
    sender: {
      type: Object as PropType<SigningPageUser>,
      default: undefined,
    },
    focusedRequiredId: {
      type: String,
      default: '',
    },
    hideFields: {
      type: Boolean,
    },
    isPreview: {
      type: Boolean,
    },
    requiredStepsLeft: {
      type: Boolean,
    },
    isSigned: {
      type: Boolean,
    },
    initials: {
      type: String,
      default: '',
    },
    verifiedInitialFieldIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  emits: ['click-link-field', 'click-sign-field', 'click-initials-field', 'loaded', 'update-field'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const imageIsLoaded = ref(false);
    const imageError = ref(false);

    const fields = computed(() => (props.page as DocumentPage)?.fields || []);

    const disableFields = computed(() => props.isPreview);

    const documentPageElementId = computed(() =>
      props.page
        ? `document-content-page-${(props.page as DocumentPage).id}`
        : 'document-content-page'
    );

    const skeletonHeight = computed(() =>
      props.page ? (props.pageWidth / props.page.pageWidth) * props.page.pageHeight : 900
    );

    const thumbUrl = computed(() => (props.page ? (props.page as DocumentPage).thumbUrl : ''));

    const showFields = computed(
      () => imageIsLoaded.value && !imageError.value && !props.hideFields
    );

    const imageDescription = (pageNumber: number) =>
      t('document-page', { 'page-number': pageNumber });

    const handleClickLinkField = (id: string) => {
      emit('click-link-field', id);
    };

    const handleClickInitialsField = (id: string) => {
      emit('click-initials-field', id);
    };

    const handleClickSignField = () => {
      emit('click-sign-field');
    };

    const handleLoaded = () => {
      imageIsLoaded.value = true;
      imageError.value = false;
      emit('loaded');
    };

    const handleImgError = () => {
      imageError.value = true;
    };

    const handleUpdateField = ({ fieldId, value }: { fieldId: string; value: string }) => {
      emit('update-field', { fieldId, value });
    };

    return {
      t,
      imageIsLoaded,
      imageError,
      fields,
      disableFields,
      documentPageElementId,
      skeletonHeight,
      thumbUrl,
      showFields,
      imageDescription,
      handleClickLinkField,
      handleClickInitialsField,
      handleClickSignField,
      handleLoaded,
      handleImgError,
      handleUpdateField,
    };
  },
});
</script>
<style lang="scss" scoped>
.document-content-page-container {
  user-select: none;
  position: relative;
  background: var(--white);
  line-height: 0;
  width: 100%;

  .document-content-page {
    position: relative;
    width: 100%;
  }

  .document-content-page-loader {
    width: 100%;
    object-fit: cover;
    pointer-events: none;
  }

  .document-content-error-page {
    position: relative;
    width: 100%;
    height: 10rem;
    display: flex;
    justify-content: center;
    align-items: center;

    .error-message {
      color: var(--text-gray);
    }
  }
}
</style>
